import { useNavigate } from 'react-router-dom';
import SectionHeader from "@/components/SectionHeader";
import { SimpleReveal } from '@/hooks/useSimpleAnimation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { cn } from '@/lib/utils';
import { useHomeData } from '../../hooks/useHomeData';
import TechnologyCard from '../TechnologyCard';

export const HomeTechnologySection = () => {
  const navigate = useNavigate();
  const { technologies } = useHomeData();
  const isMobile = useIsMobile();
  
  const handleCardClick = () => {
    navigate('/development-process');
  };
  
  const {
    activeIndex,
    setActiveIndex,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTransitioning,
    dragOffsetPercent,
    isDragging,
    containerRef
  } = useSwipeNavigation(technologies.length);
  
  return (
    <section id="features" className="bg-white section-spacing pb-24 md:pb-20 font-inter scroll-mt-24 overflow-visible">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleReveal>
          <div className="text-center mb-12">
            <SectionHeader 
              title="Our Innovation" 
              subtitle="Scientific Foundations & Mechanisms" 
              className="mb-6" 
            />
            <h2 className="sr-only" tabIndex={-1}>Technology Section</h2>
            <p className="text-section-description">
              Xenira's lipid-based drug delivery systems (LBDDS) help overcome key barriers to oral drug delivery through three core mechanisms:
            </p>
          </div>
        </SimpleReveal>
        
        <SimpleReveal delay={200}>
          {/* Desktop and tablet: Grid layout */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
              {technologies.map((tech, index) => (
                <TechnologyCard
                  key={tech.id}
                  title={tech.title}
                  description={tech.description}
                  image={tech.image}
                  onClick={handleCardClick}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>

          {/* Mobile: Swipe implementation with dots */}
          <div className="block sm:hidden">
            <div ref={containerRef} className="relative -mx-4 sm:mx-0 overflow-hidden">
              <div 
                className={cn(
                  "flex transform-gpu touch-pan-y will-change-transform",
                  isDragging ? "transition-none" : "transition-transform duration-400 ease-[cubic-bezier(0.25,1,0.5,1)]"
                )}
                style={{ 
                  transform: `translate3d(calc(-${activeIndex * 100}% + ${dragOffsetPercent}%), 0, 0)`
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {technologies.map((tech, index) => (
                  <div 
                    key={tech.id} 
                    className="basis-full shrink-0 px-6 py-2"
                    style={{ minHeight: '420px' }}
                  >
                    <TechnologyCard
                      title={tech.title}
                      description={tech.description}
                      image={tech.image}
                      isActive={index === activeIndex}
                      isMobile={true}
                      onClick={handleCardClick}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Dots */}
            <div className="flex justify-center mt-8 space-x-3">
              {technologies.map((tech, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  disabled={isTransitioning}
                  aria-pressed={index === activeIndex}
                  aria-label={`View innovation ${index + 1} of ${technologies.length}: ${tech.title}`}
                  className={`h-2 rounded-full transition-all duration-300 shadow-sm disabled:cursor-not-allowed ${
                    index === activeIndex ? 'icon-gradient-red w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </SimpleReveal>
      </div>
    </section>
  );
};
